import type {
  ScheduleActivity,
  ScheduleClassOption,
  ScheduleDiagnostic,
  ScheduleOccurrenceDraft,
  SchedulePeriod,
  ScheduleRulesConfig,
  ScheduleRunSummary,
  ScheduleSlot,
  ScheduleTask,
  ScheduleTaskDetail,
  ScheduleTaskUpsertRequest,
  ScheduleTeacherOption,
  ScheduleWorkingDay,
  TeacherSchedule
} from '@/types'
import { http } from '../request'
import {
  buildClassOptionKey,
  compareClassNames,
  createOccurrenceKey,
  isNotFoundError,
  type PageResponse,
  toIsoString,
  toScheduleRunSummary,
  toScheduleSlot,
  toTeacherSchedule
} from './shared'

type ScheduleTaskPayload = {
  name: string
  semesterId: number
  academicYearId: number
  bellScheduleId: number
  startDate: string | undefined
  endDate: string | undefined
  workingDays: Array<{
    dayOfWeek: number
    name: string
  }>
  activities: Array<{
    id: number | undefined
    courseId: number
    subjectId: number | null | undefined
    classId: number
    subgroupId: number | null | undefined
    teacherIds: number[] | undefined
    weeklyFrequency: number
    activityTags: string[]
  }>
  classes: string[]
  selectedSlotKeys: string[]
  rulesConfig: ScheduleRulesConfig | undefined
}

export const taskApi = {
  async listTasks(): Promise<PageResponse<ScheduleTask>> {
    const response = await http.get<PageResponse<ScheduleTask>>('/schedule/tasks')
    const data = response.data

    return {
      ...data,
      items: (Array.isArray(data.items) ? data.items : []).map(toTaskListItem)
    }
  },

  async getTask(id: number): Promise<ScheduleTaskDetail> {
    const response = await http.get<ScheduleTaskDetail>(`/schedule/tasks/${id}`)
    return toTaskDetail(response.data)
  },

  async createTask(payload: ScheduleTaskUpsertRequest): Promise<ScheduleTaskDetail> {
    const response = await http.post<ScheduleTaskDetail>('/schedule/tasks', toTaskPayload(payload))
    return toTaskDetail(response.data)
  },

  async updateTask(id: number, payload: ScheduleTaskUpsertRequest): Promise<ScheduleTaskDetail> {
    const response = await http.put<ScheduleTaskDetail>(`/schedule/tasks/${id}`, toTaskPayload(payload))
    return toTaskDetail(response.data)
  },

  async removeTask(id: number): Promise<void> {
    await http.delete<void>(`/schedule/tasks/${id}`)
  },

  async copyTask(id: number): Promise<ScheduleTaskDetail> {
    const response = await http.post<ScheduleTaskDetail>(`/schedule/tasks/${id}/copy`, {})
    return toTaskDetail(response.data)
  },

  async getClassSchedule(taskId: number, classId: number): Promise<ScheduleSlot[]> {
    const response = await http.get<ScheduleSlot[]>(`/schedule/tasks/${taskId}/classes/${classId}/schedule`)
    return (Array.isArray(response.data) ? response.data : []).map(toScheduleSlot)
  },

  async getTeacherSchedule(taskId: number, teacherId: number): Promise<TeacherSchedule | null> {
    try {
      const response = await http.get<TeacherSchedule>(`/schedule/tasks/${taskId}/teachers/${teacherId}/schedule`)
      return toTeacherSchedule(response.data)
    } catch (error) {
      if (isNotFoundError(error)) {
        return null
      }
      throw error
    }
  }
}

function normalizeTaskStatus(rawStatus: string): ScheduleTask['status'] {
  switch (rawStatus) {
    case 'TASK_PROCESSING':
      return 'TASK_PROCESSING'
    case 'TASK_ADJUSTING':
      return 'TASK_ADJUSTING'
    case 'TASK_PUBLISHED':
      return 'TASK_PUBLISHED'
    case 'TASK_ACTIVATED':
      return 'TASK_ACTIVATED'
    default:
      return 'TASK_DRAFT'
  }
}

/**
 * 任务卡片四段进度：配置 / 调整 / 发布 / 启用
 * TASK_DRAFT 与 TASK_PROCESSING 均落在「配置」段（PROCESSING 另由 stat-items 展示求解进度）
 */
export function taskStageFromTaskStatus(status: ScheduleTask['status']): ScheduleTask['stage'] {
  switch (status) {
    case 'TASK_DRAFT':
    case 'TASK_PROCESSING':
      return 'configure'
    case 'TASK_ADJUSTING':
      return 'adjust'
    case 'TASK_PUBLISHED':
      return 'publish'
    case 'TASK_ACTIVATED':
      return 'activate'
    default:
      return 'configure'
  }
}

function mapTaskStage(rawStatus: string, _latestRun: ScheduleRunSummary | null): ScheduleTask['stage'] {
  return taskStageFromTaskStatus(normalizeTaskStatus(rawStatus))
}

function toWorkingDay(payload: unknown): ScheduleWorkingDay {
  const day = payload as Record<string, unknown>
  return {
    dayOfWeek: Number(day.dayOfWeek ?? 0),
    name: String(day.name ?? '')
  }
}

function toPeriod(payload: unknown): SchedulePeriod {
  const period = payload as Record<string, unknown>
  return {
    id: period.id != null ? Number(period.id) : null,
    name: String(period.name ?? ''),
    period: Number(period.period ?? 0),
    startTime: String(period.startTime ?? ''),
    endTime: String(period.endTime ?? ''),
    isMorning: typeof period.isMorning === 'boolean' ? period.isMorning : null,
    sort: period.sort != null ? Number(period.sort) : null
  }
}

function toActivity(payload: unknown): ScheduleActivity {
  const activity = payload as Record<string, unknown>
  const teacherIds = Array.isArray(activity.teacherIds)
    ? activity.teacherIds.map((item) => Number(item)).filter((id) => Number.isFinite(id) && id > 0)
    : []
  const teacherNames = Array.isArray(activity.teacherNames)
    ? activity.teacherNames.map((item) => String(item)).filter((name) => name !== '')
    : []
  const resolvedTeacherIds = Array.isArray(activity.resolvedTeacherIds)
    ? activity.resolvedTeacherIds.map((item) => Number(item)).filter((id) => Number.isFinite(id) && id > 0)
    : []
  const resolvedTeacherNames = Array.isArray(activity.resolvedTeacherNames)
    ? activity.resolvedTeacherNames.map((item) => String(item)).filter((name) => name !== '')
    : []
  return {
    id: activity.id != null ? Number(activity.id) : null,
    courseId: activity.courseId != null ? Number(activity.courseId) : null,
    courseName: String(activity.courseName ?? ''),
    subjectId: activity.subjectId != null ? Number(activity.subjectId) : null,
    subjectName: activity.subjectName != null ? String(activity.subjectName) : null,
    classId: Number(activity.classId ?? 0),
    className: String(activity.className ?? ''),
    subgroupId: activity.subgroupId != null ? Number(activity.subgroupId) : null,
    subgroupName: activity.subgroupName != null ? String(activity.subgroupName) : null,
    teacherId: activity.teacherId != null ? Number(activity.teacherId) : null,
    teacherName: activity.teacherName != null ? String(activity.teacherName) : null,
    teacherIds: teacherIds.length > 0 ? teacherIds : null,
    teacherNames: teacherNames.length > 0 ? teacherNames : null,
    resolvedTeacherId:
      activity.resolvedTeacherId != null ? Number(activity.resolvedTeacherId) : null,
    resolvedTeacherName:
      activity.resolvedTeacherName != null ? String(activity.resolvedTeacherName) : null,
    resolvedTeacherIds: resolvedTeacherIds.length > 0 ? resolvedTeacherIds : null,
    resolvedTeacherNames: resolvedTeacherNames.length > 0 ? resolvedTeacherNames : null,
    weeklyFrequency: Number(activity.weeklyFrequency ?? 0),
    activityTags: Array.isArray(activity.activityTags)
      ? activity.activityTags.map((item) => String(item))
      : []
  }
}

function toTaskListItem(payload: unknown): ScheduleTask {
  const task = payload as Record<string, unknown>
  const latestRun = toScheduleRunSummary(task.latestRun)
  const rawStatus = String(task.status ?? 'TASK_DRAFT')
  return {
    id: Number(task.id ?? 0),
    name: String(task.name ?? ''),
    semesterId: Number(task.semesterId ?? 0),
    semesterName: String(task.semesterName ?? ''),
    academicYearId: Number(task.academicYearId ?? 0),
    academicYearName: String(task.academicYearName ?? ''),
    startDate: toIsoString(task.startDate),
    endDate: toIsoString(task.endDate),
    status: normalizeTaskStatus(rawStatus),
    createdAt: String(task.createdAt ?? ''),
    updatedAt: toIsoString(task.updatedAt),
    teacherCount: Number(task.teacherCount ?? 0),
    classCount: Number(task.classCount ?? 0),
    progress: Number(task.progress ?? latestRun?.progress ?? 0),
    stage: mapTaskStage(rawStatus, latestRun),
    latestRun,
    relaxationPhase: task.relaxationPhase != null ? String(task.relaxationPhase) : null,
    startupError: task.startupError != null && String(task.startupError).length > 0
      ? String(task.startupError)
      : null,
    bottleneckSummary: task.bottleneckSummary != null && String(task.bottleneckSummary).length > 0
      ? String(task.bottleneckSummary)
      : null,
    lastSessionOutcome: task.lastSessionOutcome != null ? String(task.lastSessionOutcome) : null,
    lastSessionSnapshotCount: task.lastSessionSnapshotCount != null
      ? Number(task.lastSessionSnapshotCount)
      : null,
    cardRuntime: toCardRuntime(task.cardRuntime),
    cardOutcome: toCardOutcome(task.cardOutcome)
  }
}

/** 卡片运行态映射：只接受后端三种权威 mode，其他一律视为空闲。 */
function toCardRuntime(payload: unknown): ScheduleTask['cardRuntime'] {
  if (!payload || typeof payload !== 'object') {
    return null
  }
  const runtime = payload as Record<string, unknown>
  const mode = String(runtime.mode ?? '')
  if (mode !== 'queued' && mode !== 'strict' && mode !== 'exploring') {
    return null
  }
  return {
    mode,
    queuePosition: runtime.queuePosition != null ? Number(runtime.queuePosition) : null,
    totalActivities: runtime.totalActivities != null ? Number(runtime.totalActivities) : null,
    progress: runtime.progress != null ? Number(runtime.progress) : null,
    maxPlacedActivities: runtime.maxPlacedActivities != null ? Number(runtime.maxPlacedActivities) : null
  }
}

/** 卡片结果态映射：只接受后端三种权威 type，其他一律视为无结果。 */
function toCardOutcome(payload: unknown): ScheduleTask['cardOutcome'] {
  if (!payload || typeof payload !== 'object') {
    return null
  }
  const outcome = payload as Record<string, unknown>
  const type = String(outcome.type ?? '')
  if (type !== 'succeeded' && type !== 'failed' && type !== 'stopped') {
    return null
  }
  return {
    type,
    message: String(outcome.message ?? ''),
    errorDetail: outcome.errorDetail != null ? String(outcome.errorDetail) : null,
    conflictDetail: outcome.conflictDetail != null && String(outcome.conflictDetail).length > 0
      ? String(outcome.conflictDetail)
      : null,
    similarConflictCount: outcome.similarConflictCount != null ? Number(outcome.similarConflictCount) : null,
    snapshotCount: outcome.snapshotCount != null ? Number(outcome.snapshotCount) : null,
    hasBottleneckSummary: outcome.hasBottleneckSummary === true,
    acknowledged: outcome.acknowledged === true
  }
}

function toTaskDetail(payload: unknown): ScheduleTaskDetail {
  const detail = payload as Record<string, unknown>
  const task = toTaskListItem(detail)
  const mappedActivities = Array.isArray(detail.activities) ? detail.activities.map((item) => toActivity(item)) : []
  mappedActivities.forEach(assertActivityReferenceIntegrity)
  return {
    ...task,
    exportable: Boolean(detail.exportable),
    activityCount: Number(detail.activityCount ?? 0),
    ruleCount: Number(detail.ruleCount ?? 0),
    bellScheduleId: detail.bellScheduleId != null ? Number(detail.bellScheduleId) : null,
    bellScheduleName: detail.bellScheduleName != null ? String(detail.bellScheduleName) : null,
    workingDays: Array.isArray(detail.workingDays)
      ? detail.workingDays.map((item) => toWorkingDay(item))
      : [],
    periods: Array.isArray(detail.periods) ? detail.periods.map((item) => toPeriod(item)) : [],
    activities: mappedActivities,
    classes: Array.isArray(detail.classes) ? detail.classes.map(String) : [],
    selectedSlotKeys: Array.isArray(detail.selectedSlotKeys)
      ? detail.selectedSlotKeys.map((item) => String(item))
      : [],
    rulesConfig: detail.rulesConfig as ScheduleRulesConfig | undefined,
    diagnostics: toScheduleDiagnostics(detail.diagnostics),
    sanitizationWarnings: Array.isArray(detail.sanitizationWarnings)
      ? detail.sanitizationWarnings.map((item) => String(item)).filter((item) => item.length > 0)
      : undefined
  }
}

/** 将后端结构化诊断收敛为前端稳定形态，避免缺省字段中断任务详情渲染。 */
export function toScheduleDiagnostics(payload: unknown): ScheduleDiagnostic[] {
  if (!Array.isArray(payload)) {
    return []
  }

  return payload
    .filter((item): item is Record<string, unknown> => item != null && typeof item === 'object')
    .map((item) => {
      const diagnosticPayload =
        item.payload != null && typeof item.payload === 'object' && !Array.isArray(item.payload)
          ? { ...(item.payload as Record<string, unknown>) }
          : {}

      return {
        code: String(item.code ?? ''),
        severity: String(item.severity ?? 'info'),
        phase: String(item.phase ?? ''),
        targetType: String(item.targetType ?? ''),
        targetId: item.targetId != null ? Number(item.targetId) : null,
        message: String(item.message ?? ''),
        payload: diagnosticPayload,
        suggestions: Array.isArray(item.suggestions)
          ? item.suggestions.map((suggestion) => String(suggestion)).filter(Boolean)
          : []
      }
    })
    .filter((item) => item.message.length > 0)
}

function toPlainJson<T>(value: T, errorMessage: string): T {
  try {
    return JSON.parse(JSON.stringify(value)) as T
  } catch (cause) {
    const detail = cause instanceof Error ? cause.message : String(cause)
    throw new Error(`${errorMessage}（${detail}）`, { cause })
  }
}

function toTaskPayload(payload: ScheduleTaskUpsertRequest): ScheduleTaskPayload {
  if (!Array.isArray(payload.activities)) {
    throw new Error('排课活动数据无效，请刷新后重试')
  }
  if (!Array.isArray(payload.workingDays)) {
    throw new Error('工作日数据无效，请刷新后重试')
  }

  payload.activities.forEach(assertActivityReferenceIntegrity)

  // rulesConfig 可能仍带有 Vue 响应式代理；发请求前必须收成可 JSON 序列化的纯对象，
  // 否则 JSON.stringify 抛出 circular structure 且会被前端错误处理掩盖成「更新排课任务失败」。
  const rulesConfig =
    payload.rulesConfig == null
      ? undefined
      : toPlainJson(payload.rulesConfig, '排课规则配置无法保存，请刷新页面后重试')

  return {
    name: payload.name,
    semesterId: payload.semesterId,
    academicYearId: payload.academicYearId,
    bellScheduleId: payload.bellScheduleId,
    startDate: payload.startDate ?? undefined,
    endDate: payload.endDate ?? undefined,
    workingDays: payload.workingDays.map((day) => ({
      dayOfWeek: day.dayOfWeek,
      name: day.name
    })),
    activities: payload.activities.map((activity) => ({
      id: activity.id ?? undefined,
      courseId: activity.courseId as number,
      subjectId: activity.subjectId,
      classId: activity.classId,
      subgroupId: activity.subgroupId ?? undefined,
      // 多师同堂：统一走 teacherIds 完整教师列表，不再发单值 teacherId（后端该字段仅为旧客户端兼容）；
      // 本地状态只有旧单教师字段时包装成列表，避免教师被静默丢弃
      teacherIds: Array.isArray(activity.teacherIds) && activity.teacherIds.length > 0
        ? activity.teacherIds.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)
        : activity.teacherId != null
          ? [Number(activity.teacherId)]
          : undefined,
      weeklyFrequency: activity.weeklyFrequency,
      activityTags: Array.isArray(activity.activityTags) ? [...activity.activityTags] : []
    })),
    classes: payload.classes || [],
    selectedSlotKeys: payload.selectedSlotKeys ?? [],
    rulesConfig
  }
}

function assertActivityReferenceIntegrity(activity: ScheduleActivity): void {
  if (activity.courseId == null || activity.courseId <= 0) {
    throw new Error('排课活动必须选择课程')
  }
}

function detailToDraftOccurrences(detail: ScheduleTaskDetail): ScheduleOccurrenceDraft[] {
  return detail.activities.flatMap((activity) => {
    if (activity.id == null) {
      throw new Error('任务活动缺少 id，无法生成草稿课位')
    }
    if (activity.courseId == null || activity.courseId <= 0) {
      throw new Error(`任务活动缺少有效 courseId，无法生成草稿课位：activityId=${activity.id}`)
    }
    const activityId = activity.id
    const teacherId = activity.teacherId ?? activity.resolvedTeacherId ?? null
    const teacherName = activity.teacherName ?? activity.resolvedTeacherName ?? ''
    const rawTeacherIds = activity.teacherIds ?? activity.resolvedTeacherIds ?? null
    const teacherIds = Array.isArray(rawTeacherIds) && rawTeacherIds.length > 0
      ? rawTeacherIds.filter((id): id is number => id != null && id > 0)
      : teacherId != null ? [teacherId] : []
    const rawTeacherNames = activity.teacherNames ?? null
    const teacherNames = Array.isArray(rawTeacherNames) && rawTeacherNames.length > 0
      ? rawTeacherNames.filter((name): name is string => Boolean(name))
      : teacherName ? [teacherName] : []
    return Array.from({ length: Math.max(activity.weeklyFrequency, 0) }, (_, index) => ({
      key: createOccurrenceKey(activityId, index + 1),
      taskActivityId: activityId,
      occurrenceIndex: index + 1,
      courseId: activity.courseId,
      courseName: activity.courseName,
      classId: activity.classId,
      className: activity.className,
      subgroupId: activity.subgroupId ?? null,
      subgroupName: activity.subgroupName ?? null,
      teacherId,
      teacherName: teacherNames.length > 0 ? teacherNames.join('、') : teacherName,
      teacherIds,
      teacherNames,
      duration: 1,
      placed: false,
      dayOfWeek: null,
      period: null,
      roomId: null,
      roomName: null
    }))
  })
}

export function buildDraftOccurrencesFromTask(task: ScheduleTaskDetail): ScheduleOccurrenceDraft[] {
  return detailToDraftOccurrences(task)
}

// 瀵煎嚭涓氬姟宸ュ叿鍑芥暟
export function buildScheduleTeacherOptions(task: ScheduleTaskDetail): ScheduleTeacherOption[] {
  const map = new Map<number, ScheduleTeacherOption>()
  for (const activity of task.activities) {
    // 多师同堂：每位共同任教教师都作为可选教师项
    const teacherId = activity.teacherId ?? activity.resolvedTeacherId
    const teacherName = activity.teacherName ?? activity.resolvedTeacherName
    const rawTeacherIds = activity.teacherIds ?? activity.resolvedTeacherIds ?? null
    const teacherIds = Array.isArray(rawTeacherIds) && rawTeacherIds.length > 0
      ? rawTeacherIds.filter((id): id is number => id != null && id > 0)
      : teacherId != null ? [teacherId] : []
    const teacherNames = Array.isArray(activity.teacherNames) ? activity.teacherNames : []
    teacherIds.forEach((id, index) => {
      const name = teacherNames[index] ?? (index === 0 ? teacherName : null)
      if (!name || map.has(id)) return
      map.set(id, {
        id,
        name,
        subject: activity.subjectName ?? ''
      })
    })
  }
  return sortTeacherOptionsBySubjectAndName([...map.values()], buildSubjectWeeklyFrequencyMap(task))
}

// 计算每个学科的周课时数总和（按 weeklyFrequency 加总）
export function buildSubjectWeeklyFrequencyMap(task: ScheduleTaskDetail): Map<string, number> {
  const map = new Map<string, number>()
  for (const activity of task.activities) {
    const subject = activity.subjectName ?? ''
    if (!subject) continue
    map.set(subject, (map.get(subject) ?? 0) + (activity.weeklyFrequency ?? 0))
  }
  return map
}

// 教师列表排序：同学科聚在一起 → 学科按周课时数降序 → 同学科内教师按姓名拼音升序
export function sortTeacherOptionsBySubjectAndName(
  teachers: ScheduleTeacherOption[],
  subjectFrequencyMap: Map<string, number>
): ScheduleTeacherOption[] {
  return [...teachers].sort((a, b) => {
    const freqA = subjectFrequencyMap.get(a.subject) ?? 0
    const freqB = subjectFrequencyMap.get(b.subject) ?? 0
    if (freqA !== freqB) {
      return freqB - freqA
    }
    // 周课时数相同（含都为 0）时，按学科名拼音排序，保证同学科挨在一起
    if (a.subject !== b.subject) {
      return a.subject.localeCompare(b.subject, 'zh-CN')
    }
    return a.name.localeCompare(b.name, 'zh-CN')
  })
}

export function buildScheduleClassOptions(task: ScheduleTaskDetail): ScheduleClassOption[] {
  type ClassGroup = {
    id: number
    name: string
    wholeClassSeen: boolean
    subgroups: Map<number, string>
  }

  const map = new Map<number, ClassGroup>()
  for (const activity of task.activities) {
    let group = map.get(activity.classId)
    if (!group) {
      group = {
        id: activity.classId,
        name: activity.className,
        wholeClassSeen: false,
        subgroups: new Map<number, string>()
      }
      map.set(activity.classId, group)
    }

    if (activity.subgroupId != null) {
      group.subgroups.set(
        activity.subgroupId,
        activity.subgroupName?.trim() || `#${activity.subgroupId}`
      )
    } else {
      group.wholeClassSeen = true
    }
  }

  const classOrder = new Map((task.classes ?? []).map((name, index) => [name, index]))
  const sortedGroups = [...map.values()].sort((left, right) => {
    const leftOrder = classOrder.get(left.name)
    const rightOrder = classOrder.get(right.name)
    if (leftOrder != null && rightOrder != null && leftOrder !== rightOrder) {
      return leftOrder - rightOrder
    }
    if (leftOrder != null && rightOrder == null) {
      return -1
    }
    if (leftOrder == null && rightOrder != null) {
      return 1
    }
    return compareClassNames(left.name, right.name)
  })

  return sortedGroups.flatMap<ScheduleClassOption>((group) => {
    if (group.subgroups.size > 0) {
      return [...group.subgroups.entries()]
        .sort((left, right) => compareClassNames(left[1], right[1]))
        .map(([subgroupId, subgroupName]) => ({
          id: group.id,
          classId: group.id,
          className: group.name,
          subgroupId,
          subgroupName,
          key: buildClassOptionKey(group.id, subgroupId),
          name: `${group.name} / ${subgroupName}`
        }))
    }

    if (!group.wholeClassSeen) {
      return []
    }

    return [{
      id: group.id,
      classId: group.id,
      className: group.name,
      subgroupId: null,
      subgroupName: null,
      key: buildClassOptionKey(group.id, null),
      name: group.name
    }]
  })
}
